import { useState, useEffect } from 'react';
import {
  collection,
  query,
  where,
  orderBy,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  serverTimestamp,
  onSnapshot
} from 'firebase/firestore';
import { db, auth } from '../services/firebase';

export const useGroceryLists = () => {
  const [groceryLists, setGroceryLists] = useState([]);
  const [archivedLists, setArchivedLists] = useState([]);
  const [activeList, setActiveList] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let unsubscribeActive = null;
    let unsubscribeArchived = null;

    const unsubscribeAuth = auth.onAuthStateChanged((user) => {
      if (unsubscribeActive) { unsubscribeActive(); unsubscribeActive = null; }
      if (unsubscribeArchived) { unsubscribeArchived(); unsubscribeArchived = null; }

      if (!user) {
        setGroceryLists([]);
        setArchivedLists([]);
        setActiveList(null);
        setLoading(false);
        return;
      }

      const activeQuery = query(
        collection(db, 'groceryLists'),
        where('ownerId', '==', user.uid),
        where('isArchived', '==', false),
        orderBy('updatedAt', 'desc')
      );

      const archivedQuery = query(
        collection(db, 'groceryLists'),
        where('ownerId', '==', user.uid),
        where('isArchived', '==', true),
        orderBy('updatedAt', 'desc')
      );

      unsubscribeActive = onSnapshot(
        activeQuery,
        (snapshot) => {
          const listsData = snapshot.docs.map(d => ({
            id: d.id,
            ...d.data(),
            createdAt: d.data().createdAt?.toDate?.() || null,
            updatedAt: d.data().updatedAt?.toDate?.() || null
          }));
          setGroceryLists(listsData);
          setLoading(false);
          setError(null);
        },
        (err) => {
          console.error('Error fetching active lists:', err);
          setError(err.message);
          setLoading(false);
        }
      );

      unsubscribeArchived = onSnapshot(
        archivedQuery,
        (snapshot) => {
          const listsData = snapshot.docs.map(d => ({
            id: d.id,
            ...d.data(),
            createdAt: d.data().createdAt?.toDate?.() || null,
            updatedAt: d.data().updatedAt?.toDate?.() || null
          }));
          setArchivedLists(listsData);
        },
        (err) => {
          console.error('Error fetching archived lists:', err);
        }
      );
    });

    return () => {
      unsubscribeAuth();
      if (unsubscribeActive) unsubscribeActive();
      if (unsubscribeArchived) unsubscribeArchived();
    };
  }, []);

  const createList = async (name, storeId = null, storeName = null) => {
    if (!auth.currentUser) throw new Error('Must be logged in');
    try {
      const newList = {
        name,
        ownerId: auth.currentUser.uid,
        sharedWith: [],
        items: [],
        isArchived: false,
        storeId: storeId || null,
        storeName: storeName || null,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };
      const docRef = await addDoc(collection(db, 'groceryLists'), newList);
      return { id: docRef.id, ...newList, createdAt: new Date(), updatedAt: new Date() };
    } catch (err) {
      console.error('Error creating list:', err);
      throw err;
    }
  };

  const addItemToList = async (listId, item) => {
    if (!auth.currentUser) throw new Error('Must be logged in');
    try {
      const list = groceryLists.find(l => l.id === listId) || activeList;
      if (!list) throw new Error('List not found');

      const existingIndex = list.items?.findIndex(i =>
        i.name?.toLowerCase() === item.name?.toLowerCase()
      ) ?? -1;

      let updatedItems;
      if (existingIndex !== -1) {
        updatedItems = [...list.items];
        const existing = updatedItems[existingIndex];
        updatedItems[existingIndex] = {
          ...existing,
          quantity: (existing.quantity || 0) + (item.quantity || 1)
        };
      } else {
        const newItem = {
          id: Date.now().toString(),
          name: item.name,
          quantity: item.quantity || 1,
          category: item.category || 'other',
          isChecked: false,
          notes: item.notes || null,
          sourceRecipeId: item.sourceRecipeId || null,
          sourceRecipeName: item.sourceRecipeName || null,
          sourceProductId: item.sourceProductId || null,
          productImageURL: item.productImageURL || null,
          addedBy: auth.currentUser.uid,
          addedAt: new Date().toISOString()
        };
        updatedItems = [...(list.items || []), newItem];
      }

      await updateDoc(doc(db, 'groceryLists', listId), {
        items: updatedItems,
        updatedAt: serverTimestamp()
      });
    } catch (err) {
      console.error('Error adding item:', err);
      throw err;
    }
  };

  const addItemsToList = async (listId, items) => {
    for (const item of items) {
      await addItemToList(listId, item);
    }
  };

  const toggleItem = async (listId, itemId) => {
    if (!auth.currentUser) throw new Error('Must be logged in');
    try {
      const list = groceryLists.find(l => l.id === listId) || activeList;
      if (!list) throw new Error('List not found');
      const updatedItems = list.items.map(item =>
        item.id === itemId ? { ...item, isChecked: !item.isChecked } : item
      );
      await updateDoc(doc(db, 'groceryLists', listId), {
        items: updatedItems,
        updatedAt: serverTimestamp()
      });
    } catch (err) {
      console.error('Error toggling item:', err);
      throw err;
    }
  };

  const removeItem = async (listId, itemId) => {
    if (!auth.currentUser) throw new Error('Must be logged in');
    try {
      const list = groceryLists.find(l => l.id === listId) || activeList;
      if (!list) throw new Error('List not found');
      const updatedItems = list.items.filter(item => item.id !== itemId);
      await updateDoc(doc(db, 'groceryLists', listId), {
        items: updatedItems,
        updatedAt: serverTimestamp()
      });
    } catch (err) {
      console.error('Error removing item:', err);
      throw err;
    }
  };

  const clearCheckedItems = async (listId) => {
    if (!auth.currentUser) throw new Error('Must be logged in');
    try {
      const list = groceryLists.find(l => l.id === listId) || activeList;
      if (!list) throw new Error('List not found');
      const updatedItems = list.items.filter(item => !item.isChecked);
      await updateDoc(doc(db, 'groceryLists', listId), {
        items: updatedItems,
        updatedAt: serverTimestamp()
      });
    } catch (err) {
      console.error('Error clearing items:', err);
      throw err;
    }
  };

  const uncheckAllItems = async (listId) => {
    if (!auth.currentUser) throw new Error('Must be logged in');
    try {
      const list = groceryLists.find(l => l.id === listId) || activeList;
      if (!list) throw new Error('List not found');
      const updatedItems = list.items.map(item => ({ ...item, isChecked: false }));
      await updateDoc(doc(db, 'groceryLists', listId), {
        items: updatedItems,
        updatedAt: serverTimestamp()
      });
    } catch (err) {
      console.error('Error unchecking items:', err);
      throw err;
    }
  };

  const archiveList = async (listId) => {
    if (!auth.currentUser) throw new Error('Must be logged in');
    try {
      await updateDoc(doc(db, 'groceryLists', listId), {
        isArchived: true,
        updatedAt: serverTimestamp()
      });
      if (activeList?.id === listId) setActiveList(groceryLists[0] || null);
    } catch (err) {
      console.error('Error archiving list:', err);
      throw err;
    }
  };

  const unarchiveList = async (listId) => {
    if (!auth.currentUser) throw new Error('Must be logged in');
    try {
      await updateDoc(doc(db, 'groceryLists', listId), {
        isArchived: false,
        updatedAt: serverTimestamp()
      });
    } catch (err) {
      console.error('Error unarchiving list:', err);
      throw err;
    }
  };

  const deleteList = async (listId) => {
    if (!auth.currentUser) throw new Error('Must be logged in');
    try {
      await deleteDoc(doc(db, 'groceryLists', listId));
      if (activeList?.id === listId) setActiveList(groceryLists[0] || null);
    } catch (err) {
      console.error('Error deleting list:', err);
      throw err;
    }
  };

  const renameList = async (listId, newName) => {
    if (!auth.currentUser) throw new Error('Must be logged in');
    try {
      await updateDoc(doc(db, 'groceryLists', listId), {
        name: newName,
        updatedAt: serverTimestamp()
      });
    } catch (err) {
      console.error('Error renaming list:', err);
      throw err;
    }
  };

  const updateStore = async (listId, storeId, storeName) => {
    if (!auth.currentUser) throw new Error('Must be logged in');
    try {
      await updateDoc(doc(db, 'groceryLists', listId), {
        storeId: storeId || null,
        storeName: storeName || null,
        updatedAt: serverTimestamp()
      });
    } catch (err) {
      console.error('Error updating store:', err);
      throw err;
    }
  };

  return {
    groceryLists,
    archivedLists,
    activeList,
    setActiveList,
    loading,
    error,
    createList,
    addItemToList,
    addItemsToList,
    toggleItem,
    removeItem,
    clearCheckedItems,
    uncheckAllItems,
    archiveList,
    unarchiveList,
    deleteList,
    renameList,
    updateStore
  };
};
